import { useParams, Link } from 'react-router-dom';
import { getComicById, hotComics, getCategoryName } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

const MOCK_CHAPTERS_COUNT = 50;

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  if (!comic) {
    return <NotFound />;
  }

  const categoryName = getCategoryName(comic.category);
  const relatedComics = hotComics
    .filter(c => c.id !== comic.id && c.category === comic.category)
    .slice(0, 5);

  return (
    <>
      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <Link to={`/category/${comic.category}`} className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">{categoryName}</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium truncate">{comic.title}</span>
          </nav>
        </div>
      </div>

      {/* Detail Header */}
      <section className="bg-background-50 pb-8 md:pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} - 韩漫网免费在线漫画`}
                  title={`${comic.title} - 韩漫网免费在线漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${comic.status === '已完结' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'}`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="flex items-center gap-1 text-sm text-accent-600">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm text-accent-500"></i>
                  </span>
                  {comic.rating} 分
                </span>
                <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-4">
                {comic.tags.map(tag => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                {comic.description}
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button className="flex items-center justify-center gap-2 bg-primary-500 text-white px-8 py-3 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  从第一话开始阅读
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-play-line"></i>
                  </span>
                </button>
                <button className="flex items-center justify-center gap-2 bg-background-100 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap border border-background-200/70">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bookmark-line"></i>
                  </span>
                  加入收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter List */}
      <section className="bg-background-100 py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
            <div className="flex items-center gap-2">
              <button className="text-xs px-3 py-1.5 rounded-full bg-primary-50 text-primary-600 font-medium cursor-pointer whitespace-nowrap">
                正序
              </button>
              <button className="text-xs px-3 py-1.5 rounded-full bg-background-200/70 text-foreground-500 font-medium cursor-pointer whitespace-nowrap">
                倒序
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
            {Array.from({ length: Math.min(comic.chapters, MOCK_CHAPTERS_COUNT) }, (_, i) => {
              const chNum = comic.chapters - i;
              return (
                <a
                  key={chNum}
                  href="#"
                  className="block px-4 py-2.5 rounded-md text-sm bg-white border border-background-200/70 text-foreground-700 hover:text-primary-600 hover:border-primary-200 hover:bg-primary-50/50 transition-all cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  第 {chNum} 话
                </a>
              );
            })}
          </div>

          {comic.chapters > MOCK_CHAPTERS_COUNT && (
            <div className="text-center mt-5">
              <button className="text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                加载更多章节
                <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Related */}
      <section className="bg-background-50 py-8 md:py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-900">
              相关推荐
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="false">
            {relatedComics.map(c => (
              <ComicDetailCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ComicDetailCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩漫网免费在线漫画`}
          title={`${comic.title} - 韩漫网免费在线漫画`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
        {comic.title}
      </h3>
      <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
    </Link>
  );
}